import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Eye, CalendarCheck, Info, Repeat, ClipboardCheck, Ruler } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';
import { WALKTHROUGH_MAILTO } from '../../i18n/trust';

// Customer proof. Deliberately labelled as single-customer measurement —
// the credibility comes from the honesty, not from the size of the number.
export const ProofSection = () => {
  const { t } = useLang('proof');

  return (
    <section id="proof" className="relative py-24 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]">
      <div className="absolute top-1/4 right-0 w-[460px] h-[460px] bg-[#2372D4]/8 rounded-full blur-[150px] pointer-events-none" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="max-w-3xl mb-12">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00CED1]">{t.eyebrow}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">{t.title}</h2>
          <p className="mt-4 text-base text-white/55 leading-relaxed">{t.lead}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-10" data-testid="proof-stats">
          {(t.stats || []).map((s, i) => (
            <div
              key={s.label}
              className="p-7 rounded-2xl bg-gradient-to-b from-white/[0.05] to-transparent border border-white/10"
              data-testid={`proof-stat-${i}`}
            >
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl sm:text-5xl font-bold tracking-tight text-[#00CED1]">{s.value}</span>
                <span className="text-xs uppercase tracking-wider text-white/35">{s.unit}</span>
              </div>
              <h3 className="text-base font-semibold text-white mt-3 mb-2">{s.label}</h3>
              <p className="text-sm text-white/50 leading-relaxed">{s.sub}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="p-7 rounded-2xl bg-white/[0.02] border border-white/10" data-testid="proof-also">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardCheck className="h-4 w-4 text-[#00CED1]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">{t.alsoEyebrow}</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-4">{t.alsoTitle}</h3>
            <ul className="space-y-3">
              {(t.also || []).map((m) => (
                <li key={m} className="flex items-start gap-3 text-sm text-white/60 leading-relaxed">
                  <span className="h-1.5 w-1.5 mt-2 rounded-full bg-[#00CED1] shrink-0" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-7 rounded-2xl bg-white/[0.02] border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Repeat className="h-4 w-4 text-[#00CED1]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">{t.methodEyebrow}</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-4">{t.methodTitle}</h3>
            <ul className="space-y-3">
              {(t.method || []).map((m) => (
                <li key={m} className="flex items-start gap-3 text-sm text-white/60 leading-relaxed">
                  <span className="h-1.5 w-1.5 mt-2 rounded-full bg-[#00CED1] shrink-0" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-7" data-testid="proof-methodology">
          <div className="flex items-center gap-2 mb-4">
            <Ruler className="h-4 w-4 text-[#00CED1]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">{t.methodologyTitle}</span>
          </div>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2.5">
            {(t.methodology || []).map((m) => (
              <li key={m} className="flex items-start gap-3 text-sm text-white/55 leading-relaxed">
                <span className="h-1.5 w-1.5 mt-2 rounded-full bg-white/25 shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-5 p-7 rounded-2xl bg-[#00CED1]/[0.06] border border-[#00CED1]/20 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex items-start gap-3 flex-1">
            <Info className="h-5 w-5 text-[#00CED1] mt-0.5 shrink-0" />
            <p className="text-sm text-white/65 leading-relaxed" data-testid="proof-caveat">{t.caveat}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:shrink-0">
            <Button asChild className="bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold" data-testid="proof-cta-walkthrough">
              <a href={WALKTHROUGH_MAILTO}>
                <CalendarCheck className="h-4 w-4 mr-2" /> {t.ctaWalkthrough}
              </a>
            </Button>
            <Button asChild variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="proof-cta-peek">
              <Link to="/demo">
                <Eye className="h-4 w-4 mr-2" /> {t.ctaPeek}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProofSection;
